// src/hooks/useUsuarioMutations.ts
import { useState } from 'react';
import axios from 'axios';
import { apiGet, apiPost, apiPut } from '../services/api';
import { Pessoa, PessoaIntegration, LojaAcesso, PermissionsMap } from '../types/usuarios/Pessoa';
import { UsuarioParametrosAprovacaoUpsert } from '../types/aprovacao/Aprovacao';
import { useApiLock } from './useApiLock';
import { useDragAndDrop } from './useDragAndDrop';
import { showAlert } from '../contexts/DialogContext';
import { Toast } from '../contexts/ToastContext';
import { logger } from '@/utils/logger';

type SourceColumn = 'integration' | 'active' | 'inactive';
type DragData = PessoaIntegration | Pessoa;

export interface FormApi {
  getFormattedPerson: () => Pessoa;
  isFormValid: () => boolean;
}

interface Options {
  role: string | null;
  hasPermission: (permission: string) => boolean;
  onReload: () => void;
}

export const useUsuarioMutations = ({ role, hasPermission, onReload }: Options) => {
  const [selectedPerson, setSelectedPerson] = useState<Pessoa | null>(null);
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [lojasAcesso, setLojasAcesso] = useState<LojaAcesso[]>([]);
  const [selectedLojasIds, setSelectedLojasIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { isLocked, lock, unlock } = useApiLock();

  const loadPessoaPorId = async (id: number): Promise<Pessoa> => {
    try {
      const res = await apiGet(`/v1/pessoas/${id}`);
      return res.data as Pessoa;
    } catch (err) {
      logger.error('Erro ao carregar pessoa por ID:', err);
      throw err;
    }
  };

  const loadLojasAcesso = async () => {
    try {
      // Rota de menor privilégio para o contexto de vínculo (issue #26 / backend #57):
      // exige usuarios.cadastrar/editar (ou MASTER), não lojas_acesso.listar. Retorna
      // só lojas ATIVAS com projeção mínima { id, codFilial, nomeFantasia }.
      const res = await apiGet('/v1/lojas-acesso/para-vinculo');
      setLojasAcesso(res.data);
      if (selectedPerson?.lojasAcesso) {
        setSelectedLojasIds(selectedPerson.lojasAcesso.map((loja) => loja.id));
      } else {
        setSelectedLojasIds([]);
      }
    } catch (err) {
      logger.error('Erro ao carregar lojas de acesso:', err);
    }
  };

  const updatePersonStatus = async (p: Pessoa, novoStatus: boolean) => {
    if (!p.id) return;

    const pessoaAtualizada = await loadPessoaPorId(p.id);

    const lojasAcessoIds = Array.from(
      new Set(pessoaAtualizada.lojasAcesso?.map((loja: LojaAcesso) => loja.id) || [])
    );

    const bodyToSend = {
      nome: p.nome,
      cpf: p.cpf,
      contato: p.contato || {},
      usuario: p.usuario ? { userName: p.usuario.userName, ativo: novoStatus } : undefined,
      lojasAcessoIds: lojasAcessoIds,
      permissoes: pessoaAtualizada.permissoes,
    };

    try {
      await apiPut(`/v1/pessoas/${p.id}`, bodyToSend);
      Toast.success('Usuário atualizado com sucesso!');
      onReload();
    } catch (err) {
      logger.error('Erro ao atualizar:', err);
      const errorMessage =
        (axios.isAxiosError(err) ? err.response?.data?.error : undefined) ??
        'Erro ao atualizar usuário';
      await showAlert(errorMessage, { variant: 'error' });
    }
  };

  const drag = useDragAndDrop<DragData, SourceColumn>({
    onDropHandler: async (data, source, target) => {
      if (source === target) return;

      if (source === 'integration' && target === 'active') {
        if (!hasPermission('usuarios.cadastrar') && role !== 'ROLE_MASTER') {
          await showAlert('Você não tem permissão para cadastrar usuários.', { variant: 'warning' });
          return;
        }
        const integ = data as PessoaIntegration;
        const key = `/v1/pessoas_${integ.cod_usuario}`;
        if (isLocked(key)) return;
        lock(key);
        try {
          const pessoa: Pessoa = {
            nome: integ.nome,
            cpf: integ.cpf_cnpj,
            contato: {},
            usuario: {
              userName: '',
              codUsuarioIntegracao: integ.cod_usuario,
            },
            lojasAcesso: [],
          };
          setSelectedPerson(pessoa);
          setShowSidePanel(true);
        } finally {
          unlock(key);
        }
      }

      if (source === 'active' && target === 'inactive') {
        const p = data as Pessoa;
        if (p.usuario?.role === 'ROLE_MASTER') {
          await showAlert('Usuário MASTER não podem ser movido para Inativos, caso neceário acesse "Precisa de ajuda?".', { variant: 'warning' });
          return;
        }
        const key = `/v1/pessoas/${p.id}`;
        if (isLocked(key)) return;
        lock(key);
        try {
          await updatePersonStatus(p, false);
        } finally {
          unlock(key);
        }
      }

      if (source === 'inactive' && target === 'active') {
        const p = data as Pessoa;
        const key = `/v1/pessoas/${p.id}`;
        if (isLocked(key)) return;
        lock(key);
        try {
          await updatePersonStatus(p, true);
        } finally {
          unlock(key);
        }
      }
    },
  });

  const onDragStartIntegration = (data: PessoaIntegration) => drag.onDragStart(data, 'integration');
  const onDragStartActive = (data: Pessoa) => drag.onDragStart(data, 'active');
  const onDragStartInactive = (data: Pessoa) => drag.onDragStart(data, 'inactive');

  const handleEditClick = async (p: Pessoa) => {
    try {
      const pessoaCompleta = await loadPessoaPorId(p.id!);
      setSelectedPerson(pessoaCompleta);
      setShowSidePanel(true);
    } catch (err) {
      logger.error('Erro ao carregar dados completos do usuário:', err);
      await showAlert('Erro ao abrir o formulário de edição. Tente novamente.', { variant: 'error' });
    }
  };

  const handleCancelForm = () => {
    setShowSidePanel(false);
    setSelectedPerson(null);
    setSelectedLojasIds([]);
  };

  const handleLojaChange = (lojaId: number) => {
    setSelectedLojasIds((prev) =>
      prev.includes(lojaId) ? prev.filter((id) => id !== lojaId) : [...prev, lojaId]
    );
  };

  const handleSave = async (
    permissions: PermissionsMap | undefined,
    approverParams: UsuarioParametrosAprovacaoUpsert | null,
    formApi: FormApi
  ) => {
    if (!formApi.isFormValid()) {
      await showAlert('Corrija os campos inválidos antes de salvar!', { variant: 'warning' });
      return;
    }

    setIsLoading(true);
    const bodyToSend = {
      ...formApi.getFormattedPerson(),
      lojasAcessoIds: selectedLojasIds,
      permissoes: permissions,
    };

    try {
      let usuarioIdParaParams: number | null = selectedPerson?.id ?? null;
      if (!selectedPerson?.id) {
        const res = await apiPost('/v1/pessoas', bodyToSend);
        // Tenta extrair o id da pessoa criada (se backend retornar) para
        // habilitar o 2o PUT de parametros-aprovacao na mesma sessao.
        const novoId: unknown = res?.data?.id;
        usuarioIdParaParams = typeof novoId === 'number' ? novoId : null;
        await showAlert('Sucesso! Um email com login e senha foi enviado para o usuário criado!', { variant: 'success' });
      } else {
        await apiPut(`/v1/pessoas/${selectedPerson.id}`, bodyToSend);
        Toast.success('Usuário atualizado com sucesso!');
      }

      // 2o PUT: parametros de aprovacao. So executa se houver params validos
      // e id conhecido (existente ou retornado pelo POST).
      if (approverParams !== null && usuarioIdParaParams !== null) {
        try {
          await apiPut(`/v1/permissoes/${usuarioIdParaParams}/parametros-aprovacao`, approverParams);
          Toast.success('Parâmetros de aprovação salvos!');
        } catch (errParams) {
          logger.error('Erro ao salvar parametros de aprovacao:', errParams);
          const msgParams =
            (axios.isAxiosError(errParams) ? errParams.response?.data?.error : undefined) ??
            'Erro ao salvar parâmetros de aprovação. O usuário foi salvo, mas os parâmetros não foram persistidos.';
          await showAlert(msgParams, { variant: 'error' });
          // Mantem o side panel aberto para o usuario rever os params e tentar novamente
          setIsLoading(false);
          return;
        }
      }

      setShowSidePanel(false);
      setSelectedPerson(null);
      onReload();
    } catch (err) {
      logger.error('Erro ao salvar:', err);
      const errorMessage =
        (axios.isAxiosError(err) ? err.response?.data?.error : undefined) ??
        'Erro ao salvar pessoa/usuário';
      await showAlert(errorMessage, { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    selectedPerson,
    showSidePanel,
    lojasAcesso,
    selectedLojasIds,
    isLoading,
    setSelectedLojasIds,
    loadLojasAcesso,
    onDragStartIntegration,
    onDragStartActive,
    onDragStartInactive,
    onDragOver: drag.onDragOver,
    onDrop: drag.onDrop,
    handleEditClick,
    handleSave,
    handleCancelForm,
    handleLojaChange,
  };
};
